import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useContext } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import type { UnpackResponse } from "../../../api/src/lib";
import type { ReportsController } from "../../../api/src/reports/reports.controller";
import DateInput from "../components/DateInput";
import FormFieldError from "../components/FormFieldError";
import SubmitButton from "../components/SubmitButton";
import { useAuthenticatedQuery } from "../lib/api";
import { toISODateNoTimezone } from "../lib/date";
import { downloadFileResponse } from "../lib/file";
import { getSchemaFieldLabel } from "../lib/forms";
import { NotificationContext } from "../Notifications";

export function useQueryExport13f(date: Date) {
  return useAuthenticatedQuery<
    UnpackResponse<ReportsController["exportFirm13f"]>,
    boolean
  >({
    queryKey: ["reports", "exports", "13f", toISODateNoTimezone(date)],
    queryFn: async () => true,
    queryOptions: { enabled: false },
    responseCallback: downloadFileResponse,
  });
}

const schema = yup.object({
  date: yup.date().required().label("Date"),
});

const Export13f = () => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      date: toISODateNoTimezone(new Date()) as unknown as Date,
    },
  });

  const date = watch("date");

  const { isLoading: isLoadingExport, refetch: refetchExport } =
    useQueryExport13f(date);

  const notificationContext = useContext(NotificationContext);

  const onExport = useCallback(async () => {
    try {
      // throwOnError: a plain refetch() resolves when the request failed.
      await refetchExport({ throwOnError: true });
    } catch (ex) {
      console.error("Failed to download export", ex);
      notificationContext.pushNotification({
        id: "export-download",
        header: "Download failed",
        body: "An error occurred",
        variant: "danger",
      });
    }
  }, [refetchExport, notificationContext]);

  return (
    <div>
      <p className="mb-4">
        Export 13F securities data for the firm. This report includes all
        securities marked as 13F-reportable, showing CUSIP, issuer name,
        security class, market value, and shares outstanding for the selected
        quarter-end date.
      </p>
      <Form onSubmit={handleSubmit(() => onExport())}>
        <Row>
          <Col md={6} className="mb-3">
            <Form.Group controlId="form-date">
              <Form.Label>{getSchemaFieldLabel(schema.fields.date)}</Form.Label>
              <DateInput name="date" control={control} />
              <FormFieldError field={errors.date} />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <SubmitButton label="Export CSV" isSubmitting={isLoadingExport} />
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Export13f;
